import { CompanyDetailsFormController } from '../controller/CompanyDetailsFormController';
import { BaseWebComponentElement } from '../../../../framework/BaseWebComponentElement';
import { CompanyData } from '../../../types/company-data';
import { CompanyActionContext } from '../../types/CompanyActionContext';
import { DeepPartial } from 'typeorm';
import { SubscriptionType } from '../../../../license/types/SubscriptionType';
import { CompanyType } from '../../../types/company-type';
import { getContext } from '../../../../componentHelpers/getters';
import _ from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { isString } from 'class-validator';

export class CompanyDetailsFormWebComponent extends BaseWebComponentElement {
  public controller!: CompanyDetailsFormController;

  constructor() {
    super();
    this.initialSetup({
      controllerConstructor: CompanyDetailsFormController,
    });
  }

  public get formContext(): CompanyActionContext {
    return getContext.call(this);
  }

  public set formContext(context: CompanyActionContext) {
    this.setAttribute('context', context);
  }

  public async setCompanyData(company: CompanyData) {
    await this.controller.view.setCompanyData(company);
  }

  public setEditedCompanyData(company: CompanyData) {
    this.controller.view.setEditedCompanyData(company);
  }

  public setCanBeManagedByAbove(canBeManagedByAbove: boolean) {
    this.controller.view.setCanBeManagedByAbove(canBeManagedByAbove);
  }

  public get valid$(): Observable<boolean> {
    if (this.formContext === CompanyActionContext.MyCompany) {
      return this.controller.view.formGroup.valid$;
    }

    if (this.controller.view.dom.$customFieldsForm.isCustomFieldsFormAvailable) {
      // enable / disable "Save" button based on company details main form, companyType and custom fields
      return combineLatest([
        this.controller.view.formGroup.valid$,
        this.controller.view.dom.$companyTypeFormField.valid$,
        this.controller.view.dom.$customFieldsForm.valid$,
      ]).pipe(
        map(([formGroupValid, companyTypeValid, customFieldsValid]) => {
          return formGroupValid && companyTypeValid && customFieldsValid;
        })
      );
    } else {
      // enable / disable "Save" button based on company details main form and companyType
      return combineLatest([
        this.controller.view.formGroup.valid$,
        this.controller.view.dom.$companyTypeFormField.valid$,
      ]).pipe(
        map(([formGroupValid, companyTypeValid]) => {
          return formGroupValid && companyTypeValid;
        })
      );
    }
  }

  public get value(): DeepPartial<CompanyData & { subscription: SubscriptionType; addUser: boolean }> {
    let baseCompanyRecord: DeepPartial<CompanyData & { subscription: SubscriptionType; addUser: boolean }> = {};

    const createdCompanyType = this.controller.view.dom.$companyTypeFormField.value;

    // only create company requires some extra fields
    if (this.formContext === CompanyActionContext.Create) {
      baseCompanyRecord = {
        addUser: !!(
          this.controller.view.dom.$primaryAccountEmailFormField.value ||
          this.controller.view.dom.$primaryAccountFullNameFormField.value
        ),
        specifics: {
          rights: {
            companyManager: true,
            manageCompanies: createdCompanyType === CompanyType.Partner,
          },
        },
      };
    } else if (this.formContext === CompanyActionContext.Edit) {
      baseCompanyRecord = {
        specifics: {
          rights: {
            companyManager: true,
            manageCompanies: createdCompanyType === CompanyType.Partner,
          },
        },
      };
    }

    return _.merge(baseCompanyRecord, this.getCompanyDetailsFormGroupValue());
  }

  public get hasUnsavedChanges(): boolean {
    return (
      this.controller.view.formGroup.hasUnsavedChanges ||
      this.controller.view.dom.$companyTypeFormField.hasUnsavedChanges ||
      this.controller.view.dom.$customFieldsForm.hasUnsavedChanges ||
      this.controller.view.dom.$manageNetworksFormField.hasUnsavedChanges ||
      this.controller.view.dom.$canBeManagedByAboveFormField.hasUnsavedChanges ||
      this.controller.view.dom.$allowDownloadQuarantineByAboveFormField.hasUnsavedChanges
    );
  }

  public get isReadOnly(): boolean {
    return this.controller.view.isReadOnly;
  }

  public refreshInitialFormValue(): void {
    return this.controller.view.refreshInitialValues();
  }

  public resetWarnings(): void {
    return this.controller.view.resetWarnings();
  }

  public updateManageNetworkSection(showWarnings: boolean): void {
    this.controller.view.updateManageNetworksToggleSection(showWarnings);
  }

  public setupMdrContactDetailsSection(): void {
    this.controller.view.setupMdrContactDetailsSection().then();
  }

  public async uploadLogo() {
    await this.controller.uploadLogo();
  }

  public resetLogoField() {
    this.controller.view.dom.$logoFormField.value = null;
  }

  public getCompanyType(): CompanyType {
    return this.controller.view.dom.$companyTypeFormField.value as CompanyType;
  }

  public getCanManageNetworks(): boolean {
    return this.controller.view.dom.$manageNetworksFormField.value;
  }

  public getCanBeManagedByAbove(): boolean {
    return this.controller.view.dom.$canBeManagedByAboveFormField.value;
  }

  public async updateCustomFieldsValues(): Promise<void> {
    if (!this.controller.view.dom.$customFieldsForm.isCustomFieldsFormAvailable) {
      return;
    }

    return this.controller.view.dom.$customFieldsForm.updateCustomFieldsValues();
  }

  public discardFormChanges(): void {
    return this.controller.view.discardFormChanges();
  }

  private getCompanyDetailsFormGroupValue(): object {
    const formGroupValue = _.cloneDeep(this.controller.view.formGroup.value);

    this.convertMdrPhoneNumber(formGroupValue);

    return formGroupValue;
  }

  private convertMdrPhoneNumber(formGroupValue: object): void {
    // Convert possible phoneNumber string to object format
    const phoneNumber = _.get(formGroupValue, ['specifics', 'mdrContactInformation', 'phoneNumber'], null);

    if (isString(phoneNumber)) {
      const phoneNumberObject = { countryCode: null, subscriberNumber: phoneNumber };
      _.set(formGroupValue, 'specifics.mdrContactInformation.phoneNumber', phoneNumberObject);
    }
  }
}
